import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import usePagination from '@mui/material/usePagination';
import './CancelledSessionTable.css';
import { Avatar, AvatarGroup, Box, MenuItem, Select, Typography } from '@mui/material';
import { useState } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Initialize dayjs plugins for timezone conversion
dayjs.extend(utc);
dayjs.extend(timezone);

const SessionTypeColor = {
  one_on_one: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'One-On-One' },
  group: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Group Sessions' },
  prime: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'Prime Sessions' },
};

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

// Helper function to convert UTC time to local time
const toLocalTime = utcTime => {
  if (!utcTime) return null;
  return dayjs.utc(utcTime).local();
};

// Helper function to format text (capitalize first letter and replace underscores with spaces)
const formatText = text => {
  if (!text) return '---';
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function CancelledSessionTable({ cancelledSessionData }) {
  const [page, setPage] = useState(1);
  const [filteredCancelledSessionData, setFilteredCancelledSessionData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  console.log('cancelledSessionData--->', cancelledSessionData);

  useEffect(() => {
    if (cancelledSessionData && Array.isArray(cancelledSessionData)) {
      setFilteredCancelledSessionData(cancelledSessionData);
    } else {
      setFilteredCancelledSessionData([]);
    }
  }, [cancelledSessionData]);

  const { items } = usePagination({
    count: Math.ceil(filteredCancelledSessionData?.length / rowsPerPage),
    onChange: (event, value) => setPage(value),
  });

  // Determine the rows to display on the current page
  const startIndex = (page - 1) * rowsPerPage;
  const currentRows = filteredCancelledSessionData.slice(startIndex, startIndex + rowsPerPage);

  const handleCellClick = (event, id) => {
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      // Use the teacher_id from the session data or from the first session if available
      const teacherId = cancelledSessionData[0]?.teacher_id;

      if (teacherId) {
        // If we have the teacher ID, use the proper path format
        navigate(`/admin/users/instructor/${teacherId}/session-details/${id}`);
      } else {
        // Get the current URL path which should contain the teacher ID from the current page
        const pathParts = window.location.pathname.split('/');
        const currentTeacherId = pathParts.find(
          (part, index) =>
            pathParts[index - 1] === 'instructor-details' || pathParts[index - 1] === 'instructor'
        );

        if (currentTeacherId) {
          navigate(`/admin/users/instructor/${currentTeacherId}/session-details/${id}`);
        } else {
          // Last resort - navigate to teacher list since we can't determine the teacher ID
          navigate('/admin/users/instructor');
        }
      }
    }
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';

    const localStartTime = toLocalTime(startTime);
    const localEndTime = localStartTime.add(lengthInMinutes, 'minute');

    // Different scenarios
    const startHour = localStartTime.format('h:mm');
    const endHour = localEndTime.format('h:mm');
    const startPeriod = localStartTime.format('A');
    const endPeriod = localEndTime.format('A');

    // Handling AM/PM transition
    if (startPeriod !== endPeriod) {
      return `${startHour} ${startPeriod} - ${endHour} ${endPeriod}`;
    }

    // Same period (AM or PM)
    return `${startHour} - ${localEndTime.format('h:mm A')}`;
  };

  const formatDate = dateString => {
    if (!dateString) return '---';
    return toLocalTime(dateString).format('DD-MM-YYYY');
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="paginated table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                height: '25px',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell align="left">Session Type</TableCell>
              <TableCell align="left">Learner Name</TableCell>
              <TableCell align="left">Date</TableCell>
              <TableCell align="left">Timing</TableCell>
              <TableCell align="left">Session Koins</TableCell>
              <TableCell align="left">Cancelled On</TableCell>
              {/* <TableCell align="left">Cancelled By</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F) !important',
                height: '25px !important',
                fontFamily: 'Poppins !important',
                fontSize: '14px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: 'normal !important',
                padding: '9px 16px',
              },
            }}
          >
            {currentRows.length > 0 ? (
              currentRows.map(row => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  sx={{
                    '& td': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#E9F1FF !important',
                      cursor: 'pointer',
                    },
                  }}
                >
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {row?.subject ? row?.subject : '---'}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box
                      style={{
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '150px',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        border: `1px solid ${SessionTypeColor[row?.session_type]?.color}`,
                        backgroundColor: SessionTypeColor[row?.session_type]?.bgcolor,
                        color: SessionTypeColor[row?.session_type]?.color,
                        fontFamily: 'Poppins !important',
                        fontSize: '14px !important',
                        fontStyle: 'normal !important',
                        fontWeight: '400 !important',
                        lineHeight: 'normal !important',
                      }}
                    >
                      <Typography className="level-text">
                        {row?.session_type ? formatText(row?.session_type) : '---'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    <Box className="flex items-center gap-4">
                      <AvatarGroup
                        max={4}
                        total={row?.students?.length || 0}
                        sx={{
                          '& .MuiAvatar-root': {
                            width: 25,
                            height: 25,
                          },
                          '& .MuiAvatarGroup-avatar': {
                            width: 25,
                            height: 25,
                            fontSize: '0.75rem',
                            backgroundColor: 'grey.300',
                            color: 'grey.700',
                          },
                        }}
                      >
                        {row?.students?.map(student => (
                          <Avatar
                            key={student?.id}
                            alt={student?.user?.name}
                            src={student?.user?.profile_image || ''}
                          />
                        ))}
                      </AvatarGroup>
                      <Typography>
                        {row?.students && row.students.length > 0
                          ? row.students[0]?.user?.name
                          : '---'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatDate(row?.start_time)}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatTimeInterval(row?.start_time, row?.length_in_minutes)}
                  </TableCell>
                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {row?.session_koins || ''}
                  </TableCell>

                  <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {formatDate(row?.updated_at)}
                  </TableCell>
                  {/* <TableCell align="left" onClick={e => handleCellClick(e, row.id)}>
                    {row?.cancelled_by ? formatText(row?.cancelled_by) : '---'}
                  </TableCell> */}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: '#666',
                    }}
                  >
                    No Cancelled Session available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Showing
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            sx={{
              height: '30px',
              minWidth: '20px',
              fontFamily: 'Poppins',
              fontSize: '14px',
              '& .MuiSelect-select': {
                padding: '6px 4px 4px 8px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: '14px',
              color: '#666',
            }}
          >
            of {filteredCancelledSessionData?.length} Sessions
          </Typography>
        </Box>
        <nav>
          <List>
            {items.map(({ page: itemPage, type, selected, ...item }, index) => {
              let children = null;

              if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                children = '…';
              } else if (type === 'page') {
                children = (
                  <button
                    type="button"
                    style={{
                      fontWeight: selected ? 'bold' : undefined,
                      margin: '0 5px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: selected ? '#000' : '#666',
                      backgroundColor: selected ? '#f0f0f0' : 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                    }}
                    {...item}
                  >
                    {itemPage}
                  </button>
                );
              } else {
                children = (
                  <button
                    type="button"
                    style={{
                      margin: '0 5px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: '#666',
                      border: 'none',
                      backgroundColor: 'transparent',
                    }}
                    {...item}
                  >
                    {type === 'previous' ? 'Previous' : 'Next'}
                  </button>
                );
              }

              return <li key={index}>{children}</li>;
            })}
          </List>
        </nav>
      </Box>
    </>
  );
}
